import {
  Outlet,
  RootRoute,
  Route,
  Router,
  RouterProvider,
  createMemoryHistory,
  createBrowserHistory,
} from "@tanstack/react-router";
import { AppShell } from "@/components/layout/AppShell";
import { DashboardPage } from "@/pages/Dashboard";
import { SetupCenterPage } from "@/pages/SetupCenter";
import { StrategyHubPage } from "@/pages/strategy/StrategyHub";
import { EntityTransitionPage } from "@/pages/strategy/EntityTransition";
import { CashAllocationPage } from "@/pages/strategy/CashAllocation";
import { HireFamilyPage } from "@/pages/strategy/HireFamily";
import { RetirementPage } from "@/pages/strategy/Retirement";
import { TaxPlaysPage } from "@/pages/strategy/TaxPlays";
import { TaxesPage } from "@/pages/Taxes";
import { TaxIntakePage } from "@/pages/TaxIntake";
import { MoneyPage } from "@/pages/Money";
import { CompliancePage } from "@/pages/Compliance";
import { CalendarPage } from "@/pages/Calendar";
import { DocumentsPage } from "@/pages/Documents";
import { PeoplePage } from "@/pages/People";
import { PersonalPage } from "@/pages/Personal";
import { SettingsPage } from "@/pages/Settings";
import { InvoicesPage } from "@/pages/Invoices";
import { EmailPage } from "@/pages/Email";
import { NotFoundPage } from "@/pages/NotFound";
import TasksPage from "@/pages/Tasks";
import NotesPage from "@/pages/Notes";

const rootRoute = new RootRoute({
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
  notFoundComponent: NotFoundPage,
});

const indexRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/",
  component: DashboardPage,
});

const setupRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/setup",
  component: SetupCenterPage,
});

const strategyRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/strategy",
  component: StrategyHubPage,
});

const strategyEntityRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/strategy/entity",
  component: EntityTransitionPage,
});

const strategyCashRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/strategy/cash",
  component: CashAllocationPage,
});

const strategyHireFamilyRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/strategy/hire-family",
  component: HireFamilyPage,
});

const strategyRetirementRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/strategy/retirement",
  component: RetirementPage,
});

const strategyTaxPlaysRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/strategy/tax-plays",
  component: TaxPlaysPage,
});

const taxesRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/taxes",
  component: TaxesPage,
});

const taxIntakeRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/tax-intake",
  component: TaxIntakePage,
});

const moneyRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/money",
  component: MoneyPage,
});

const complianceRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/compliance",
  component: CompliancePage,
});

const calendarRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/calendar",
  component: CalendarPage,
});

const documentsRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/documents",
  component: DocumentsPage,
});

const peopleRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/people",
  component: PeoplePage,
});

const personalRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/personal",
  component: PersonalPage,
});

const settingsRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: SettingsPage,
});

const invoicesRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/invoices",
  component: InvoicesPage,
});

const emailRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/email",
  component: EmailPage,
});

const tasksRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/tasks",
  component: TasksPage,
});

const notesRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/notes",
  component: NotesPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  setupRoute,
  strategyRoute,
  strategyEntityRoute,
  strategyCashRoute,
  strategyHireFamilyRoute,
  strategyRetirementRoute,
  strategyTaxPlaysRoute,
  taxesRoute,
  taxIntakeRoute,
  moneyRoute,
  complianceRoute,
  calendarRoute,
  documentsRoute,
  peopleRoute,
  personalRoute,
  settingsRoute,
  invoicesRoute,
  emailRoute,
  tasksRoute,
  notesRoute,
]);

export const router = new Router({
  routeTree,
  history:
    typeof window === "undefined"
      ? createMemoryHistory({ initialEntries: ["/"] })
      : createBrowserHistory(),
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export function AppRouter() {
  return <RouterProvider router={router} />;
}
